import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "./firebase";

/** Maior lado da imagem de categoria depois de reduzida, em pixels. */
const CATEGORY_IMAGE_MAX_PX = 1024;

/** O que o painel aceita antes de reduzir. A imagem enviada fica bem menor. */
export const CATEGORY_IMAGE_MAX_INPUT_BYTES = 15 * 1024 * 1024;

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

/**
 * Reduz a imagem no navegador antes de enviar.
 *
 * A imagem de categoria aparece em grade, pequena, nos dois aplicativos, e eles
 * leem só `imageUrl` (não os derivados do `processUploadedImage`). Mandar a foto
 * de 4 MB do celular faria cada cliente baixar 4 MB por categoria. Reduzida a
 * 1024 px em WebP, ela fica na casa das dezenas de KB.
 */
export async function prepareImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, CATEGORY_IMAGE_MAX_PX / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("O navegador não conseguiu processar a imagem.");
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/webp", 0.85),
  );
  // Safari antigo não gera WebP pelo canvas e devolve PNG pesado; aí vale JPEG.
  if (blob && blob.type === "image/webp") return blob;
  const jpeg = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", 0.85),
  );
  if (!jpeg) throw new Error("O navegador não conseguiu processar a imagem.");
  return jpeg;
}

/**
 * Envia a imagem da categoria ao Storage e devolve a URL pública.
 *
 * O caminho é `categories/{arquivo}`, o mesmo que o aplicativo do vendedor já
 * usava, e a regra do Storage só aceita a escrita de quem tem o claim `admin`.
 * O nome leva o slug e a hora, para nunca sobrescrever a imagem que a categoria
 * usa hoje: se o salvamento falhar depois do envio, a categoria continua
 * apontando para a imagem antiga, que ainda existe.
 */
export async function uploadCategoryImage(
  file: File,
  slug: string,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const blob = await prepareImage(file);
  const extension = blob.type === "image/webp" ? "webp" : "jpg";
  const safeSlug = slug.replace(/[^a-z0-9-]/g, "").slice(0, 60) || "categoria";
  const target = ref(storage, `categories/${safeSlug}-${Date.now()}.${extension}`);

  const task = uploadBytesResumable(target, blob, {
    contentType: blob.type,
    // O nome do arquivo muda a cada envio, então a imagem pode ficar em cache
    // para sempre: uma troca de imagem nunca reaproveita o mesmo endereço.
    cacheControl: "public, max-age=31536000, immutable",
  });

  await new Promise<void>((resolve, reject) => {
    task.on(
      "state_changed",
      (snapshot) => onProgress?.(snapshot.totalBytes ? snapshot.bytesTransferred / snapshot.totalBytes : 0),
      reject,
      () => resolve(),
    );
  });

  return getDownloadURL(task.snapshot.ref);
}

/** Traduz o erro do Storage para o que o administrador precisa ler. */
export function describeUploadError(error: unknown): string {
  const code = (error as { code?: string })?.code ?? "";
  if (code === "storage/unauthorized") {
    return "Sua conta não tem permissão para enviar imagens. Saia e entre de novo para atualizar o acesso de administrador.";
  }
  if (code === "storage/canceled") return "O envio da imagem foi cancelado.";
  if (code === "storage/retry-limit-exceeded" || code === "storage/unknown") {
    return "Não foi possível enviar a imagem. Verifique a conexão e tente de novo.";
  }
  return error instanceof Error && error.message
    ? error.message
    : "Não foi possível enviar a imagem.";
}
